import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
// evertel
import { Col, Modal, ModalBody, Row, Text, ModalHeader, Nav, NavItem, NavLink, TabContent, TabPane, Icon } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { InviteTabs, OpenInviteProps } from './InviteContext'
import { ContactInviteForm, DepartmentInviteForm, RoomInviteForm } from './elements'
import { DepartmentController } from '@evertel/department'
import { formatNumber } from '@evertel/utils'
import { SendInvitesBody } from '@evertel/invites'
import { CurrentUserController } from '@evertel/blue-user'

interface InviteModalProps extends OpenInviteProps {
    visible: boolean
    inviteBody?: SendInvitesBody
    inviteType?: InviteTabs
    invitedRooms?: { label: string, value: number }[]
    inviteDepartmentId?: number | undefined
    onClose: () => void
    onCloseDone: () => void
}

const InviteModal: React.FC<InviteModalProps> = observer(({
    visible,
    onClose,
    onCloseDone,
    inviteBody,
    inviteType,
    invitedRooms,
    inviteDepartmentId
}) => {

    const currentUserController = useService(CurrentUserController)
    const sessionService = useService(SessionService, [])

    const departmentId = inviteDepartmentId || sessionService.selectedDepartmentId
    const departmentController = useService(DepartmentController, [departmentId])

    // Determine if we're in the special case mode
    const isRoomManagementInviteGuest = inviteType === 'room-management-invite-guest'

    // Set initial active tab, handling the special case
    const initialTab = isRoomManagementInviteGuest ? 'room' : (inviteType as InviteTabs || 'department')
    const [activeTab, setActiveTab] = useState<InviteTabs>(initialTab)

    useEffect(() => {
        // this modal stays mounted for the app's lifetime (see InviteContextProvider),
        // so gate on `visible` to refetch the seat counts each time it's reopened
        if (!departmentId || !visible) return

        departmentController.init(departmentId)

        departmentController.fetchAvailableCapacities()

    }, [departmentController, departmentId, visible])

    useEffect(() => {
        // Update active tab when inviteType changes, handling the special case
        const newTab = isRoomManagementInviteGuest ? 'room' : (inviteType as InviteTabs || activeTab)
        setActiveTab(newTab)
    }, [inviteType, isRoomManagementInviteGuest])

    const tabs = [
        {
            id: 'department',
            name: 'Agency Member',
            description: 'Invite agency personnel',
            allowedCount: formatNumber(departmentController.capacities?.verifiedMemberCapacity),
            usedCount: formatNumber(departmentController.capacities?.verifiedMembersCount),
            allowedRoles: ['executive', 'management'],
            component: DepartmentInviteForm
        },
        {
            id: 'room',
            name: 'Room Guest',
            description: 'Invite outside people',
            allowedCount: '',
            usedCount: '',
            allowedRoles: ['executive', 'management'],
            component: RoomInviteForm
        },
        {
            id: 'contact',
            name: 'Direct Contact',
            description: 'Invite to DM',
            allowedCount: '',
            usedCount: '',
            allowedRoles: ['executive', 'management', 'employee'],
            component: ContactInviteForm
        }
    ]

    // Determine which tabs to show - if in special mode, only show the guest tab regardless of role
    const filteredTabs = isRoomManagementInviteGuest
        ? tabs.filter(tab => tab.id === 'room')
        : tabs.filter(tab => tab.allowedRoles.includes(currentUserController.selectedDepartmentRole))

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            onCloseDone={onCloseDone}
            size="lg"
        >
            <ModalHeader title={`Invite People to ${activeTab === 'contact' ? 'DM' : departmentController.department.name}`} />
            <ModalBody className="">
                <Nav
                    variant="box"
                    role="tablist">
                    {filteredTabs.map(tab =>
                        <NavItem
                            key={tab.id}
                        >
                            <NavLink
                                active={activeTab === tab.id}
                                className="p-4"
                                onClick={() => setActiveTab(tab.id as InviteTabs)}>
                                <Col>
                                    <Row>
                                        <Icon
                                            name="check-circle"
                                            type={(activeTab === tab.id) ? 'solid' : 'light'}
                                            color={(activeTab === tab.id) ? 'success' : 'muted'}
                                            size={18}
                                            className="me-2"
                                        />
                                        <Text>
                                            {tab.name}
                                        </Text>
                                    </Row>
                                    <Row className="ms-4">
                                        <Text
                                            color="body-color"
                                            className="mt-3"
                                            style={{
                                                fontWeight: 'normal'
                                            }}>
                                            {tab.description}
                                        </Text>

                                    </Row>
                                    <Text
                                        color="body-color"
                                        className="mt-3"
                                        style={{
                                            fontWeight: 'normal'
                                        }}>
                                        {(tab.usedCount && tab.allowedCount) && `${tab.usedCount} / ${tab.allowedCount}`}
                                    </Text>
                                </Col>
                            </NavLink>
                        </NavItem>
                    )}
                </Nav>
                {/* content -------------------------------------------------------------- */}
                <TabContent>
                    {tabs.map(tab =>
                        <TabPane
                            key={tab.id}
                            role="tabpanel"
                            visible={activeTab === tab.id}
                            className="mt-4">
                            <tab.component
                                departmentId={departmentId}
                                inviteBody={inviteBody}
                                invitedRooms={invitedRooms}
                                onClose={onClose}
                                role={currentUserController.selectedDepartmentRole}
                            />
                        </TabPane>
                    )}
                </TabContent>
            </ModalBody>
        </Modal>
    )
})

export { InviteModal }
